import React, { useCallback, useMemo, useState } from 'react'
import { Select } from 'antd'
import { QueryKey, useQuery } from '@tanstack/react-query'
import { debounce, map } from 'lodash'
import { FilterOption } from '../../../queries/storeItemTransactions/fetchTransactionFilterOptions'

const { Option } = Select

type Props = {
	value: number | null
	onChange: (value: number | null) => void
	placeholder: string
	queryKey: (search: string | null) => QueryKey
	fetcher: (search: string | null) => Promise<FilterOption[]>
	/**
	 * Zoznam sa nacitava po strankach, takze zvolena hodnota (napr. z URL po obnoveni stranky)
	 * v nom nemusi byt. Aby Select nezobrazil hole ID, stranka doda nazov z nacitanych riadkov.
	 */
	fallbackLabel?: string | null
	width?: number
}

const RemoteSelectFilter = (props: Props) => {
	const {
		value, onChange, placeholder, queryKey, fetcher, fallbackLabel, width
	} = props

	const [search, setSearch] = useState<string | null>(null)

	const { data: options, isLoading } = useQuery({
		queryKey: queryKey(search),
		queryFn: () => fetcher(search),
		keepPreviousData: true
	})

	const debouncedSearch = useCallback(debounce((searchTerm: string) => setSearch(searchTerm || null), 300), [])

	const selectOptions = useMemo(() => {
		const loadedOptions = options || []

		if (value && !loadedOptions.some((option) => option.value === value)) {
			return [{ value, label: fallbackLabel || `#${value}` }, ...loadedOptions]
		}

		return loadedOptions
	}, [options, value, fallbackLabel])

	return (
		<Select
			className={'dropDown'}
			size={'middle'}
			style={{ minWidth: width || 240 }}
			placeholder={placeholder}
			value={value ?? undefined}
			onChange={(selected: number) => onChange(selected ?? null)}
			onSearch={debouncedSearch}
			onBlur={() => setSearch(null)}
			showSearch
			allowClear
			loading={isLoading}
			filterOption={false}
			notFoundContent={null}
		>
			{map(selectOptions, (option) => (
				<Option key={option.value} value={option.value}>
					{option.label}
				</Option>
			))}
		</Select>
	)
}

export default RemoteSelectFilter
